import {
  AfterContentInit,
  Component,
  computed,
  ContentChild,
  ContentChildren,
  EventEmitter,
  Input,
  Output,
  QueryList,
  signal,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PopoverAnchorDirective } from '@progress/kendo-angular-tooltip';
import { injectDestroyRef } from '@qpoint/utils';
import { map, startWith } from 'rxjs/operators';

import { QPointTabNavDetailTemplateDirective } from './tab-nav-detail-template.directive';
import { QPointTabNavMenuItemDirective } from './tab-nav-menu-item.directive';

export type TabNavItemNameValidatorFn = (name: string) => string[] | null;
export type TabNavItemState = 'EDIT';

@Component({
    selector: 'qpoint-tab-nav-item',
    template: '',
    imports: []
})
export class QPointTabNavItemComponent implements AfterContentInit {
  private destroyRef = injectDestroyRef();
  private _errors= signal<string[] | null>([]);
  errors = this._errors.asReadonly();
  private _editMode = signal(false);
  editMode = this._editMode.asReadonly();

  menuItems = signal<QPointTabNavMenuItemDirective[]>([])

  showMenu = computed(() => {
    return this.menuItems()?.length > 0 && !this.editMode();
  })

  @ContentChild(QPointTabNavDetailTemplateDirective, {read: TemplateRef}) detailTemplate: TemplateRef<any>;
  @ContentChildren(QPointTabNavMenuItemDirective) menuItemsQuery: QueryList<QPointTabNavMenuItemDirective>;

  @Input()
  public id: string | number;

  @Input()
  public label: string;

  @Input()
  public icon: string;

  @Input()
  public destroyOnHide: boolean = true;

  @Input() canEditName = false;

  @Input() set inputErros(errors: string[]) {
    this._errors.set(errors);
  }

  @Input() nameChangeValidatorFn: TabNavItemNameValidatorFn = () => null;

  @Input() menuDisabled = false;

  @Input() disabled = false;

  @Output() tabNameChange = new EventEmitter<string>;

  ngAfterContentInit() {
    this.menuItemsQuery.changes
      .pipe(
        map(d => d.toArray()),
        startWith(this.menuItemsQuery.toArray() ?? []),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
      next: data => {
        this.menuItems.set(data ?? []);
      }
    })
  }

  setEditMode(editMode: boolean) {
    this._editMode.set(editMode);
  }

  validateTabName(name: string, anchor: PopoverAnchorDirective) {
    this._errors.set(this.nameChangeValidatorFn(name));

    if((this.errors() || []).length > 0) {
      anchor.show();
    } else {
      anchor.hide();
    }
  }

  handleTabNameChange(newName: string) {
    if(this.nameChangeValidatorFn(newName) !== null) {
      this._errors.set(this.nameChangeValidatorFn(newName));
      return;
    }

    this._errors.set([]);

    this.tabNameChange.emit(newName);
    this._editMode.set(false);
  }

  cancel() {
    this._editMode.set(false);
    this._errors.set([]);
  }
}
